import type { GQL_ID, NestedFilter } from '@@partner:types:graphql'

import React, { useState } from 'react'
import { Button, Col, FlexboxGrid, InputNumber, Panel, SelectPicker } from 'rsuite'

import { notifyOk } from '@@partner:helpers/notification'
import { BulkUpdateSlotInput, useMutateBulkUpdateSlots } from '@@partner:graphql/mutation/slot/bulkUpdateSlots.gql'

import './SlotBulkUpdatePanel.scss'

type TimeUnit = 'minutes' | 'hours' | 'days'

const TIME_UNIT_OPTIONS: { label: string, value: TimeUnit }[] = [
  { label: 'Минуты', value: 'minutes' },
  { label: 'Часы', value: 'hours' },
  { label: 'Сутки', value: 'days' }
]

const timeUnitToSeconds = (value: number, unit: TimeUnit): number => {
  switch (unit) {
  case 'minutes': return value * 60
  case 'hours': return value * 3600
  case 'days': return value * 86400
  }
}

interface TimeOffsetState {
  value: number | null
  unit: TimeUnit
}

interface SlotBulkUpdatePanelProps {
  show: boolean
  selectedIds: GQL_ID[]
  filter?: NestedFilter
  onSuccess?: () => void
}

export default function SlotBulkUpdatePanel ({
  show,
  selectedIds,
  filter,
  onSuccess
}: SlotBulkUpdatePanelProps) {
  const [ state, setState ] = useState<BulkUpdateSlotInput>({})
  const [ startOffset, setStartOffset ] = useState<TimeOffsetState>({ value: null, unit: 'hours' })
  const [ endOffset, setEndOffset ] = useState<TimeOffsetState>({ value: null, unit: 'hours' })

  const [ bulkUpdate, { loading } ] = useMutateBulkUpdateSlots()

  const handleFieldChange = (field: keyof BulkUpdateSlotInput) => (value: number | string | null) => {
    setState(prev => ({ 
      ...prev, 
      [field]: value !== null ? Number(value) : undefined 
    }))
  }

  const handleStartOffsetValueChange = (value: number | string | null) => {
    setStartOffset(prev => ({ ...prev, value: value !== null ? Number(value) : null }))
  }

  const handleStartOffsetUnitChange = (unit: TimeUnit | null) => {
    if (unit) setStartOffset(prev => ({ ...prev, unit }))
  }

  const handleEndOffsetValueChange = (value: number | string | null) => {
    setEndOffset(prev => ({ ...prev, value: value !== null ? Number(value) : null }))
  }

  const handleEndOffsetUnitChange = (unit: TimeUnit | null) => {
    if (unit) setEndOffset(prev => ({ ...prev, unit }))
  }

  const handleUpdateSlots = async () => {
    if (loading) return
    
    // Проверяем что есть либо выбранные ID, либо фильтр
    const hasIds = selectedIds.length > 0
    const hasFilterData = filter && Object.keys(filter).length > 0
    
    if (!hasIds && !hasFilterData) return

    const slotData: BulkUpdateSlotInput = Object.fromEntries(
      Object.entries(state).filter(([ _, v ]) => v != null)
    )

    // Добавляем смещения времени
    if (startOffset.value !== null) {
      slotData.ts_start_offset = timeUnitToSeconds(startOffset.value, startOffset.unit)
    }
    if (endOffset.value !== null) {
      slotData.ts_end_offset = timeUnitToSeconds(endOffset.value, endOffset.unit)
    }

    if (Object.keys(slotData).length === 0) {
      return
    }

    // Если есть выбранные ID - используем их, иначе фильтр
    const variables = hasIds && !hasFilterData
      ? { ids: selectedIds.map(id => id.toString()), newData: slotData }
      : { filter, newData: slotData }

    await bulkUpdate({ variables })
    notifyOk('Слоты успешно обновлены')
    setState({})
    setStartOffset({ value: null, unit: 'hours' })
    setEndOffset({ value: null, unit: 'hours' })
    onSuccess?.()
  }

  const handleClear = () => {
    setState({})
    setStartOffset({ value: null, unit: 'hours' })
    setEndOffset({ value: null, unit: 'hours' })
  }

  if (!show) {
    return null
  }

  const hasChanges = Object.values(state).some(v => v != null) ||
    startOffset.value != null || endOffset.value != null
  const hasFilter = filter && Object.keys(filter).length > 0
  const canApply = hasChanges && (selectedIds.length > 0 || hasFilter)

  return (
    <Panel 
      shaded
      className="SlotBulkUpdatePanel my-4"
    >
      <FlexboxGrid justify="space-between" align="middle">
        <FlexboxGrid.Item>
          <FlexboxGrid className="SlotBulkUpdatePanel__Fields">
            <Col>
              <p className="SlotBulkUpdatePanel__Label">Лимит заказов</p>
              <InputNumber
                value={state.orders_capacity ?? ''}
                onChange={handleFieldChange('orders_capacity')}
                placeholder="Кол-во заказов"
                min={0}
                style={{ width: 150 }}
              />
            </Col>
            <Col>
              <p className="SlotBulkUpdatePanel__Label">Лимит позиций</p>
              <InputNumber
                value={state.position_capacity ?? ''}
                onChange={handleFieldChange('position_capacity')}
                placeholder="Кол-во позиций"
                min={0}
                style={{ width: 150 }}
              />
            </Col>
            <Col>
              <p className="SlotBulkUpdatePanel__Label">Таймаут (мин)</p>
              <InputNumber
                value={state.timeout ?? ''}
                onChange={handleFieldChange('timeout')}
                placeholder="Минуты"
                min={0}
                style={{ width: 120 }}
              />
            </Col>
            <Col>
              <p className="SlotBulkUpdatePanel__Label">Смещение начала</p>
              <FlexboxGrid>
                <InputNumber
                  value={startOffset.value ?? ''}
                  onChange={handleStartOffsetValueChange}
                  placeholder="±"
                  style={{ width: 80 }}
                />
                <SelectPicker
                  data={TIME_UNIT_OPTIONS}
                  value={startOffset.unit}
                  onChange={handleStartOffsetUnitChange}
                  cleanable={false}
                  searchable={false}
                  style={{ width: 100, marginLeft: 4 }}
                />
              </FlexboxGrid>
            </Col>
            <Col>
              <p className="SlotBulkUpdatePanel__Label">Смещение конца</p>
              <FlexboxGrid>
                <InputNumber
                  value={endOffset.value ?? ''}
                  onChange={handleEndOffsetValueChange}
                  placeholder="±"
                  style={{ width: 80 }}
                />
                <SelectPicker
                  data={TIME_UNIT_OPTIONS}
                  value={endOffset.unit}
                  onChange={handleEndOffsetUnitChange}
                  cleanable={false}
                  searchable={false}
                  style={{ width: 100, marginLeft: 4 }}
                />
              </FlexboxGrid>
            </Col>
          </FlexboxGrid>
        </FlexboxGrid.Item>
        <FlexboxGrid.Item>
          <FlexboxGrid className="SlotBulkUpdatePanel__Actions">
            <span className="SlotBulkUpdatePanel__Selected">
              {hasFilter ? 'По фильтру' : `Выбрано: ${selectedIds.length}`}
            </span>
            {hasChanges && (
              <Button 
                appearance="subtle"
                onClick={handleClear}
              >
                Сбросить
              </Button>
            )}
            <Button 
              size="lg" 
              color="green" 
              appearance="primary"
              onClick={handleUpdateSlots}
              loading={loading}
              disabled={!canApply}
            >
              Применить
            </Button>
          </FlexboxGrid>
        </FlexboxGrid.Item>
      </FlexboxGrid>
    </Panel>
  )
}
